import { useState } from "react";
import { Button } from "semantic-ui-react";
import { TextField } from "@mui/material";
import { DateTimePicker, MuiPickersUtilsProvider } from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";
import { format, parseISO } from "date-fns";
import { displayStringFromFHIRReference } from "./ReferenceEntry";
import { getDateTimeDisplay } from "./PeriodEntry";
import { DisplayFromFHIRMarkdown } from "./MarkdownEntry";
import ExpandToAddOrEdit from "../components/ExpandToAddOrEdit";

const displayStringFromFHIRAnnotation = (note) => {
  if (note === undefined || note === null || note === '') {
    return ""
  }
  if (typeof note === "object") {
    try {
      if (note.text) {
        let authorDisplay = "";
        if (note.authorString) {
          authorDisplay = note.authorString;
        } else if (note.authorReference) {
          authorDisplay = displayStringFromFHIRReference(note.authorReference);
        }
        let timeDisplay = "";
        if (note.time) {
          timeDisplay = " " + getDateTimeDisplay(note.time);
        }
        let modifierDisplay = "";
        if (authorDisplay || timeDisplay) {
          modifierDisplay = " (" + authorDisplay + timeDisplay + ")";
        }
        return note.text + modifierDisplay;
      }
    } catch (e) { console.log(e); }
  }
  if (typeof note === "string") {
    return `${note} [ERROR: Annotation data not in expected Annotation datatype.]`
  }
  return "[ERROR: Annotation data not interpretable.]";
}

const DisplayFromFHIRAnnotation = ({ annotation }) => {
  let noteDisplay = displayStringFromFHIRAnnotation(annotation);
  return <DisplayFromFHIRMarkdown markdown={noteDisplay} />
}

const DisplayElementNotes = ({ element }) => {
  if (!element) {
    return <></>;
  }
  if (Array.isArray(element)) {
    return <>
      {element.map((note, noteIndex) => {
        return <div key={noteIndex}>
          <b>Note: </b><DisplayFromFHIRAnnotation annotation={note} />
          <br /><br />
        </div>
      })}
    </>
  }
  if (element.note && Array.isArray(element.note)) {
    return <>
      {element.note.map((note, noteIndex) => {
        return <div key={noteIndex}>
          <b>Note: </b><DisplayFromFHIRAnnotation annotation={note} />
          <br /><br />
        </div>
      })}
    </>
  }
}

const CalendarModal = ({ startingValue, elementName, calendarOpen, setCalendarOpen, setResourceState }) => {
  return <div className="calendarModal">
    <MuiPickersUtilsProvider utils={DateFnsUtils}>
      <DateTimePicker
        format="yyyy-MM-dd HH:mm:ss"
        value={startingValue.time ? parseISO(startingValue.time, "yyyy-MM-dd HH:mm:ss") : null}
        emptyLabel="yyyy-MM-dd HH:mm:ss"
        open={calendarOpen}
        onClose={() => setCalendarOpen(false)}
        onChange={(e) => {
          let newTime = format(e, "yyyy-MM-dd") + "T" + format(e, "HH:mm:ss");
          let newAnnotation = { authorString: "", time: newTime, text: "" };
          if (startingValue.authorReference) { newAnnotation.authorReference = startingValue.authorReference; }
          if (startingValue.authorString) { newAnnotation.authorString = startingValue.authorString; }
          if (startingValue.text) { newAnnotation.text = startingValue.text; }
          setResourceState(prevState => { return { ...prevState, [elementName]: newAnnotation } });
        }}
      />
    </MuiPickersUtilsProvider></div>
}

const AnnotationEntry = ({ elementName, fieldLabel, startingValue, setResourceState, dataEntryStyle, startCollapsed }) => {

  let autoAuthor = false;
  let editAuthor = false;
  let autoTime = false;
  let editTime = false;
  if (dataEntryStyle) {
    if (dataEntryStyle.includes("autoAuthor")) {
      autoAuthor = true;
    }
    if (dataEntryStyle.includes("editAuthor")) {
      editAuthor = true;
    }
    if (dataEntryStyle.includes("autoTime")) {
      autoTime = true;
    }
    if (dataEntryStyle.includes("editTime")) {
      editTime = true;
    }
  }

  if (!startingValue) {
    startingValue = {};
    if (autoAuthor) {
      startingValue.authorString = "TBD: This will be replaced with username display.";
    }
    if (autoTime) {
      let currentTime = new Date();
      startingValue.time = format(currentTime, "yyyy-MM-dd") + "T" + format(currentTime, "HH:mm:ss");
    }
  }

  const [calendarOpen, setCalendarOpen] = useState(false);
  const [startCollapsedState, setStartCollapsedState] = useState(startCollapsed);

  if (startCollapsedState) {
    return <div>
      <p>
        <b>{fieldLabel}: </b>
        {startingValue && displayStringFromFHIRAnnotation(startingValue)}
        &nbsp;&nbsp;
        <ExpandToAddOrEdit startingValue={startingValue} setStartCollapsedState={setStartCollapsedState} />
      </p>
    </div>
  } else {
    return <div><p style={{ marginBottom: "2px" }}><b>{fieldLabel}: </b></p>
      {editAuthor && <TextField style={{ width: "100%" }} className="inputField" type='text'
        label={'Note Author'}
        size="small" variant='outlined' value={startingValue.authorString || startingValue.authorReference?.display || ''}
        onChange={(e) => {
          let newAnnotation = { authorString: e.target.value, time: "", text: "" };
          if (startingValue.authorReference) { newAnnotation.authorReference = startingValue.authorReference; }
          if (startingValue.time) { newAnnotation.time = startingValue.time; } else { delete newAnnotation.time; }
          if (startingValue.text) { newAnnotation.text = startingValue.text; } else { delete newAnnotation.text; }
          if (!e.target.value && !startingValue.authorReference && !startingValue.time && !startingValue.text) {
            newAnnotation = {};
          }
          setResourceState(prevState => { return { ...prevState, [elementName]: newAnnotation } });
        }} />
      }
      {editTime && <div>
        <TextField style={{ width: "100%", marginTop: "16px", maxWidth: "300px" }}
          className="inputField" type='text' label="Note Time" size="small" variant='outlined'
          value={startingValue.time || ""}
          onChange={(e) => {
            let newTime = e.target.value
            let newAnnotation = { authorString: "", time: newTime, text: "" };
            if (startingValue.authorReference) { newAnnotation.authorReference = startingValue.authorReference; }
            if (startingValue.authorString) { newAnnotation.authorString = startingValue.authorString; } else { delete newAnnotation.authorString; }
            if (startingValue.text) { newAnnotation.text = startingValue.text; } else { delete newAnnotation.text; }
            if (!e.target.value && !startingValue.authorReference && !startingValue.authorString && !startingValue.text) {
              newAnnotation = {};
            }
            setResourceState(prevState => { return { ...prevState, [elementName]: newAnnotation } });
          }} />
        <Button style={{ marginTop: "18px", marginLeft: "6px", fontSize: "28px", padding: "0px", backgroundColor: "white" }} className="calendarButton"
          content="📅" onClick={() => { setCalendarOpen(true) }} />
        <CalendarModal startingValue={startingValue}
          elementName={elementName} calendarOpen={calendarOpen}
          setCalendarOpen={setCalendarOpen} setResourceState={setResourceState} />
      </div>
      }
      <TextField style={{ width: "100%" }} className="inputField" type='text' label={'Note Text'}
        multiline size="small" variant='outlined' value={startingValue.text || ''}
        onChange={(e) => {
          let newAnnotation = { authorString: "", time: "", text: e.target.value };
          if (startingValue.authorReference) { newAnnotation.authorReference = startingValue.authorReference; }
          if (startingValue.time) { newAnnotation.time = startingValue.time; } else { delete newAnnotation.time; }
          if (startingValue.authorString) { newAnnotation.authorString = startingValue.authorString; } else { delete newAnnotation.authorString; }
          if (!e.target.value && !startingValue.authorReference && !startingValue.authorString && !startingValue.time) {
            newAnnotation = {};
          }
          setResourceState(prevState => { return { ...prevState, [elementName]: newAnnotation } });
        }} />
      {startingValue && <div><b>Note Text will display as: </b><DisplayFromFHIRMarkdown markdown={startingValue.text} /><br /></div>}
    </div>
  }
};

export default AnnotationEntry;
export { displayStringFromFHIRAnnotation, DisplayFromFHIRAnnotation, DisplayElementNotes };